import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import { formatNOK } from "@/lib/format";
import { SrIcon } from "@/components/ui/SrIcon";
import { useConvertCostToTask } from "@/hooks/use-budget-assignments";
import { useFreelancerProjectsEnabled } from "@/hooks/use-freelancer-projects";
import { useBudgetLineFreelancers, type BudgetLineFreelancerRow } from "@/hooks/use-budget-line-freelancers";
import { canTagFreelancer } from "@/lib/freelancer-projects";
import { BudgetLineFreelancers } from "@/components/freelancers/BudgetLineFreelancers";
import type { BudgetLineRow } from "@/hooks/use-project-detail";

interface Props {
  costLines: BudgetLineRow[];
  projectId: string;
  readOnly: boolean;
  hourlyRate: number;
  /** Sum av godkjente bilag per budsjettlinje (id → { amount, count }). */
  vouchersByBudgetLine: Map<string, { amount: number; count: number }>;
  /** VIB-2927: edit_budget-tilgang, styrer frilansvelgeren på eksterne linjer. */
  canAssignHours: boolean;
}

/**
 * Kollapsbar kostnadsseksjon nederst i Timetildeling-fanen. Samler alle
 * fastpris-poster (cost_type='external' eller intern uten timer) i én
 * tabell separert fra time-oppgavene per fase. Inspirert av V3-prototypens
 * "Kostnader"-disclosure i variant3.jsx (linje 519-636).
 */
export function BudgetCostsSection({
  costLines,
  projectId,
  readOnly,
  hourlyRate,
  vouchersByBudgetLine,
  canAssignHours,
}: Props) {
  const [open, setOpen] = useState(false);
  const freelancersOn = useFreelancerProjectsEnabled();
  const { data: freelancerLinks = [] } = useBudgetLineFreelancers(projectId, freelancersOn && open);

  if (costLines.length === 0) return null;

  // VIB-681: interne fastpris-linjer (cost_type='internal' + 0 timer) kan også
  // konverteres til timer-oppgaver, ikke bare eksterne.
  const isConvertableLine = (bl: BudgetLineRow) =>
    bl.cost_type === "external" ||
    (bl.cost_type === "internal" && (bl.quantity_hours ?? 0) === 0);
  const convertableCount = costLines.filter(isConvertableLine).length;

  return (
    <div className="border-t">
      <div className="flex items-center gap-3 p-4">
        <span className="text-sm text-muted-foreground">
          <strong className="text-foreground font-semibold">
            {costLines.length} kostnader
          </strong>
          {convertableCount > 0 && (
            <>
              {" · "}
              {convertableCount} kan konverteres til oppgaver
            </>
          )}
        </span>
        <div className="flex-1" />
        <Button
          variant="outline"
          size="sm"
          onClick={() => setOpen((v) => !v)}
          className="h-7 text-xs"
        >
          <SrIcon
            name={open ? "chevron-down" : "chevron-right"}
            className="w-3 h-3 mr-1"
          />
          {open ? "Skjul kostnader" : "Vis kostnader"}
        </Button>
      </div>

      {open && (
        <div className="border-t bg-muted/20">
          {/* Subhead */}
          <div className="px-4 pt-3 pb-2 flex flex-wrap items-baseline gap-3">
            <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
              Kostnader
            </span>
            <span className="text-xs text-muted-foreground">
              Ikke-time-poster fra tilbudet — konverter til oppgaver hvis noe skal time-føres mot dem.
            </span>
          </div>

          {/* Kolonner */}
          <div className="grid grid-cols-[1fr_200px] gap-4 px-4 py-2 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground border-b">
            <span>Post</span>
            <span className="text-right">Handling</span>
          </div>

          {costLines.map((bl) => (
            <CostRow
              key={bl.id}
              bl={bl}
              projectId={projectId}
              readOnly={readOnly}
              hourlyRate={hourlyRate}
              attachedVouchers={vouchersByBudgetLine.get(bl.id)}
              freelancers={
                freelancersOn && canTagFreelancer(bl)
                  ? {
                      links: freelancerLinks.filter((l) => l.budget_line_id === bl.id),
                      canEdit: canAssignHours && !readOnly,
                    }
                  : null
              }
            />
          ))}
        </div>
      )}
    </div>
  );
}

interface CostRowProps {
  bl: BudgetLineRow;
  projectId: string;
  readOnly: boolean;
  hourlyRate: number;
  attachedVouchers: { amount: number; count: number } | undefined;
  /** VIB-2927: null = linjen kan ikke ha frilanser (eller flagget er av). */
  freelancers: { links: BudgetLineFreelancerRow[]; canEdit: boolean } | null;
}

function CostRow({ bl, projectId, readOnly, hourlyRate, attachedVouchers, freelancers }: CostRowProps) {
  const amount = bl.amount ?? 0;
  // VIB-681: konverter-knappen er nå tilgjengelig både for eksterne kostnader
  // og interne fastpris-linjer (0 timer). Konvertering setter cost_type=internal
  // og flagger is_converted_from_cost for audit.
  const isConvertable =
    bl.cost_type === "external" ||
    (bl.cost_type === "internal" && (bl.quantity_hours ?? 0) === 0);

  const voucherCount = attachedVouchers?.count ?? 0;
  const voucherAmount = attachedVouchers?.amount ?? 0;
  const coveragePct = amount > 0 ? Math.min(100, Math.round((voucherAmount / amount) * 100)) : 0;

  return (
    <div className="grid grid-cols-[1fr_200px] gap-4 px-4 py-3 items-center text-sm border-b border-border/40 last:border-b-0">
      <div className="min-w-0">
        <div className="flex items-center gap-2 min-w-0">
          <SrIcon name="receipt" className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
          <span className="font-medium truncate">{bl.description}</span>
          {voucherCount > 0 && (
            <span
              className={cn(
                "text-[10px] px-1.5 py-0.5 rounded font-medium shrink-0",
                coveragePct >= 100
                  ? "bg-[#D9F5E8] text-[#035C39]"
                  : coveragePct > 0
                    ? "bg-[#FEF3C7] text-[#78350F]"
                    : "bg-muted text-muted-foreground",
              )}
            >
              {voucherCount} bilag
            </span>
          )}
        </div>
        {freelancers && (
          <BudgetLineFreelancers
            budgetLineId={bl.id}
            lineName={bl.description ?? ""}
            projectId={projectId}
            links={freelancers.links}
            canEdit={freelancers.canEdit}
          />
        )}
      </div>

      <div className="flex justify-end">
        {isConvertable && !readOnly ? (
          <ConvertCostButton bl={bl} projectId={projectId} hourlyRate={hourlyRate} />
        ) : (
          <span className="text-xs text-muted-foreground italic">
            {readOnly ? "—" : "Fastpris"}
          </span>
        )}
      </div>
    </div>
  );
}

function ConvertCostButton({
  bl,
  projectId,
  hourlyRate,
}: {
  bl: BudgetLineRow;
  projectId: string;
  hourlyRate: number;
}) {
  const [open, setOpen] = useState(false);
  const amount = bl.amount ?? 0;
  const suggestedHours = hourlyRate > 0 ? Math.round((amount / hourlyRate) * 10) / 10 : 0;
  const [hoursInput, setHoursInput] = useState<string>(
    suggestedHours > 0 ? String(suggestedHours) : "",
  );
  const convertMut = useConvertCostToTask();

  function openDialog() {
    setHoursInput(suggestedHours > 0 ? String(suggestedHours) : "");
    setOpen(true);
  }

  function handleConvert() {
    const hours = Number(hoursInput);
    if (!Number.isFinite(hours) || hours <= 0) return;
    convertMut.mutate(
      { budgetLineId: bl.id, projectId, hours },
      { onSuccess: () => setOpen(false) },
    );
  }

  const hoursValid = Number.isFinite(Number(hoursInput)) && Number(hoursInput) > 0;

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        onClick={openDialog}
        className="h-7 text-xs"
        disabled={convertMut.isPending}
      >
        <SrIcon name="plus" className="w-3 h-3 mr-1" />
        Konverter til oppgave
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Konverter til oppgave</DialogTitle>
            <DialogDescription>
              "{bl.description}" blir en intern oppgave som kan tildeles personer med timer.{" "}
              {amount > 0 && <>Beløp på linjen er {formatNOK(amount)} (beholdes uendret).</>}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-2 py-2">
            <Label htmlFor="convert-hours-bottom">Antall timer</Label>
            <Input
              id="convert-hours-bottom"
              type="number"
              min={0}
              step={0.5}
              value={hoursInput}
              onChange={(e) => setHoursInput(e.target.value)}
              placeholder={suggestedHours > 0 ? String(suggestedHours) : "0"}
              autoFocus
            />
            {suggestedHours > 0 && (
              <p className="text-[12px] text-muted-foreground">
                Forslag basert på {formatNOK(hourlyRate)}/t timesats: {suggestedHours}t.
              </p>
            )}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setOpen(false)}
              disabled={convertMut.isPending}
            >
              Avbryt
            </Button>
            <Button onClick={handleConvert} disabled={!hoursValid || convertMut.isPending}>
              Konverter
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
